import React, { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import type { Player } from '../types';

interface PlayerSearchProps {
  players: Player[];
  onSearch: (filteredPlayers: Player[]) => void;
  placeholder?: string;
  className?: string;
}

const PlayerSearch: React.FC<PlayerSearchProps> = ({
  players,
  onSearch,
  placeholder = 'Search players...',
  className = '',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'all' | 'name' | 'club' | 'position'>('all');

  const filteredPlayers = useMemo(() => {
    if (!searchTerm.trim()) return players;

    const term = searchTerm.toLowerCase().trim();

    return players.filter((player) => {
      switch (searchField) {
        case 'name':
          return player.name.toLowerCase().includes(term);
        case 'club':
          return player.club?.toLowerCase().includes(term);
        case 'position':
          return player.positions?.some((pos) => pos.toLowerCase().includes(term)) ||
                 player.main_position?.toLowerCase().includes(term);
        case 'all':
        default:
          return (
            player.name.toLowerCase().includes(term) ||
            player.club?.toLowerCase().includes(term) ||
            player.positions?.some((pos) => pos.toLowerCase().includes(term)) ||
            player.main_position?.toLowerCase().includes(term) ||
            player.state?.toLowerCase().includes(term) ||
            player.league?.toLowerCase().includes(term) ||
            player.competitions?.some((competition) => competition.toLowerCase().includes(term))
          );
      }
    });
  }, [players, searchTerm, searchField]);

  useEffect(() => {
    onSearch(filteredPlayers);
  }, [filteredPlayers, onSearch]);

  const handleClear = () => {
    setSearchTerm('');
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            ×
          </button>
        )}
      </div>
      
      <div className="flex gap-2">
        <select
          value={searchField}
          onChange={(e) => setSearchField(e.target.value as 'all' | 'name' | 'club' | 'position')}
          className="px-3 py-1 rounded bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <option value="all">All Fields</option>
          <option value="name">Name</option>
          <option value="club">Club</option>
          <option value="position">Position</option>
        </select>
        
        {searchTerm && (
          <div className="flex items-center text-sm text-gray-400">
            Found: <span className="text-green-400 font-semibold ml-1">{filteredPlayers.length}</span> players
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSearch;
